import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Trophy, History, ChevronRight } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import LineupBuilder from "@/components/alliance/LineupBuilder";
import StancePanel from "@/components/alliance/StancePanel";
import { Leaderboard, FightResults } from "@/components/alliance/AllianceWarsTabs";

export default function AllianceWars() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [war, setWar] = useState(null);
  const [visible, setVisible] = useState(true);
  const [myAlliance, setMyAlliance] = useState(null);
  const [isLeader, setIsLeader] = useState(false);
  const [view, setView] = useState("overview");
  
  useEffect(() => {
    loadWarData();
  }, []);
  
  const loadWarData = async () => {
    try {
      const [warRes, allianceRes] = await Promise.all([
        api.get("/alliance-wars/current"),
        api.get("/alliance")
      ]);
      
      setVisible(Boolean(warRes.data.visible));
      setWar(warRes.data.active ? warRes.data : null);
      setMyAlliance(allianceRes.data.alliance || null);
      
      if (allianceRes.data.alliance) {
        setIsLeader(allianceRes.data.alliance.leader_id === user.user_id);
      }
    } catch (e) {
      console.error("Error loading war data:", e);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) return <Loader label="Reading the battlefield…" />;
  if (!visible) return <Navigate to="/alliance" replace />;
  
  if (!war) {
    return (
      <div data-testid="alliance-wars-inactive-page">
        <PageHeader title="Alliance Wars" subtitle="Compete for glory and rewards" />
        
        {/* Previous Wars tab even when no active war */}
        <div data-testid="alliance-wars-inactive-tabs" className="flex gap-2 mb-6 border-b border-amber-900/30">
          <TabButton active={view === "overview"} onClick={() => setView("overview")}>
            Overview
          </TabButton>
          <TabButton active={view === "history"} onClick={() => setView("history")} icon={History}>
            Previous Wars
          </TabButton>
        </div>
        
        {view === "history" ? (
          <PreviousWars />
        ) : (
          <Empty label="No Alliance Wars event is currently scheduled.">
            <div className="mt-8 max-w-2xl mx-auto text-left">
              <h3 className="font-cinzel text-base text-amber-400 uppercase tracking-wide mb-4">How Alliance Wars Works</h3>
              <div className="space-y-2 text-zinc-400 text-sm">
                <p><span className="text-amber-300 font-semibold">Duration:</span> 10 days (3-day setup + 7-day battles)</p>
                <p><span className="text-amber-300 font-semibold">Eligibility:</span> Alliances with 5+ members and 14+ non-retired gladiators</p>
                <p><span className="text-amber-300 font-semibold">Combat Rules:</span> Gladiators fight using raw stats and selected combat stances only. No gear, beasts, or skill trees are utilized — pure gladiator vs gladiator only.</p>
                <p><span className="text-amber-300 font-semibold">Setup Phase:</span> Leaders pick 14 unique fighters (2 per round, max 3 per lanista). Owners set combat tactics for each class matchup.</p>
                <p><span className="text-amber-300 font-semibold">Battle Phase:</span> Daily fights at 5:00 PM (7 rounds total, round-robin format)</p>
                <p><span className="text-amber-300 font-semibold">Rewards:</span></p>
                <ul className="list-disc list-inside ml-4 space-y-1 text-xs text-zinc-500">
                  <li>Winner: 100 gold per alliance member</li>
                  <li>Participation: 10 gold per alliance member</li>
                  <li>MVP Lanista (most combined fighter wins): 100 gold bonus and player trophy</li>
                </ul>
              </div>
            </div>
          </Empty>
        )}
      </div>
    );
  }
  
  // Calculate current day (Day 1-10)
  const startDate = new Date(war.started_at);
  const now = new Date();
  const daysSinceStart = Math.floor((now - startDate) / (1000 * 60 * 60 * 24)) + 1;
  const currentDay = Math.min(Math.max(daysSinceStart, 1), 10);
  
  // Active war exists
  const isSetupPhase = war.status === "setup";
  const isCompletedPhase = war.status === "completed";
  const isInWarAlliance = myAlliance && war.participating_alliances?.some(a => {
    // Handle both string IDs and object format
    const allianceId = typeof a === 'string' ? a : a.id;
    return allianceId === myAlliance.id;
  });
  
  // Determine phase label
  const phaseLabel = isSetupPhase ? "Setup Phase" : isCompletedPhase ? "Completed" : "Battles in Progress";
  const dayLabel = isCompletedPhase ? "Event Complete" : `Day ${currentDay} of 10`;
  
  return (
    <div data-testid="alliance-wars-page">
      {/* Alliance Wars Header Banner */}
      <div className="relative mb-6 overflow-hidden border border-amber-900/40 rounded-sm">
        <img 
          src="/alliance-wars-header.png" 
          alt="Alliance Wars - Two gladiators clash in the arena" 
          className="w-full h-44 sm:h-60 object-cover object-center" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
        <h2 className="absolute bottom-3 left-5 font-cinzel text-2xl sm:text-3xl text-amber-200 tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
          Alliance Wars
        </h2>
        <div className="absolute bottom-3 right-5 font-manrope text-sm text-amber-400/90 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
          {dayLabel} — {phaseLabel}
        </div>
      </div>
      
      {/* Winner Banner for Completed Wars */}
      {isCompletedPhase && (war.winner_id || war.tied) && (
        <div data-testid="war-winner-banner" className="border border-amber-700/50 bg-amber-950/20 p-5 mb-6 rounded-sm text-center">
          <div className="text-xs text-zinc-500 uppercase tracking-widest font-manrope mb-1">
            {war.tied ? "Alliance Wars Co-Champions" : "Alliance Wars Champion"}
          </div>
          {war.tied ? (
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              {war.winners?.map((w, i) => (
                <span key={w.id} className="inline-flex items-center gap-1">
                  {i > 0 && <span className="text-zinc-600 font-manrope text-sm">&</span>}
                  <Link 
                    to={`/alliance/${w.id}`}
                    className="inline-flex items-center gap-1.5 font-cinzel text-lg sm:text-xl text-amber-300 uppercase tracking-wide hover:text-amber-200 transition-colors"
                  >
                    {w.tag && <span className="text-zinc-500">[{w.tag}]</span>}
                    {w.name}
                    <ChevronRight size={16} className="text-amber-500/60" />
                  </Link>
                </span>
              ))}
              <div className="w-full text-[10px] text-zinc-500 font-manrope mt-1">
                Tied — reward split: {war.winner_reward_per_member || 50}g per member
              </div>
            </div>
          ) : (
            <Link 
              to={`/alliance/${war.winner_id || war.final_standings?.winner}`}
              className="inline-flex items-center gap-2 font-cinzel text-xl sm:text-2xl text-amber-300 uppercase tracking-wide hover:text-amber-200 transition-colors"
              data-testid="war-winner-link"
            >
              {war.winner_tag && <span className="text-zinc-500">[{war.winner_tag}]</span>}
              {war.winner_name || "Champion"}
              <ChevronRight size={18} className="text-amber-500/60" />
            </Link>
          )}
          {war.final_standings?.mvp?.length > 0 && (
            <div className="text-xs text-zinc-400 mt-2 font-manrope">
              MVP Lanista(s): {war.final_standings.mvp.map(m => m.lanista_name || m.fighter_name).join(", ")} ({war.final_standings.mvp[0]?.total_wins} total wins)
            </div>
          )}
        </div>
      )}
      
      {/* Event Status Banner */}
      <EventStatusBanner war={war} currentDay={currentDay} />

      {/* Permanent Combat Rules Disclaimer */}
      <div className="border border-amber-900/40 bg-amber-950/10 p-5 mb-6 rounded-sm">
        <div>
          <h3 className="font-cinzel text-sm text-amber-300 uppercase tracking-wide font-semibold mb-1">
            Combat Rules: Gladiator vs Gladiator Only
          </h3>
          <p className="text-xs text-zinc-400 leading-relaxed">
            Alliance Wars battles are resolved using <strong className="font-semibold text-zinc-300">raw gladiator stats and selected combat stances only</strong>. All equipped <strong className="font-semibold text-zinc-300">gear</strong>, <strong className="font-semibold text-zinc-300">beasts</strong>, and <strong className="font-semibold text-zinc-300">skill trees</strong> are disabled inside this arena.
          </p>
        </div>
      </div>
      
      {/* Not Participating Warning - only show during active/setup phases */}
      {!isInWarAlliance && !isCompletedPhase && (
        <div className="border border-amber-900/40 bg-amber-950/10 p-4 mb-6 rounded-sm">
          <div className="flex items-center gap-3 text-amber-400/90 text-sm">
            <span className="font-manrope">
              {myAlliance 
                ? "Your alliance is not participating in this event (requires 5+ members and 14+ gladiators across all members)"
                : "You must be in an alliance to participate in Alliance Wars"}
            </span>
          </div>
        </div>
      )}
      
      {/* Navigation Tabs */}
      <div data-testid="war-tabs" className="flex gap-2 mb-6 border-b border-amber-900/30 overflow-x-auto">
        <TabButton active={view === "overview"} onClick={() => setView("overview")}>
          Overview
        </TabButton>
        
        {isLeader && isSetupPhase && isInWarAlliance && (
          <TabButton active={view === "lineup"} onClick={() => setView("lineup")}>
            Submit Lineup
          </TabButton>
        )}
        
        {isSetupPhase && isInWarAlliance && (
          <TabButton active={view === "stances"} onClick={() => setView("stances")}>
            Set Stances
          </TabButton>
        )}
        
        {!isSetupPhase && (
          <>
            <TabButton active={view === "leaderboard"} onClick={() => setView("leaderboard")}>
              Leaderboard
            </TabButton>
            
            <TabButton active={view === "fights"} onClick={() => setView("fights")}>
              Fight Results
            </TabButton>
          </>
        )}
        
        <TabButton active={view === "history"} onClick={() => setView("history")} icon={History}>
          Previous Wars
        </TabButton>
      </div>
      
      {/* Content Views */}
      {view === "overview" && (isInWarAlliance || isCompletedPhase) && <OverviewTab war={war} myAlliance={myAlliance} isInWarAlliance={isInWarAlliance} />}
      {view === "lineup" && isLeader && isInWarAlliance && <LineupBuilder war={war} myAlliance={myAlliance} onUpdate={loadWarData} />}
      {view === "stances" && isInWarAlliance && <StancePanel war={war} onUpdate={loadWarData} />}
      {view === "leaderboard" && <Leaderboard war={war} />}
      {view === "fights" && <FightResults war={war} />}
      {view === "history" && <PreviousWars />}
      {!isInWarAlliance && view === "overview" && !isCompletedPhase && (
        <PublicEventView war={war} currentDay={currentDay} phaseLabel={phaseLabel} />
      )}
    </div>
  );
}

// Tab Button Component
function TabButton({ active, onClick, icon: Icon, children }) {
  const testId = typeof children === "string"
    ? `alliance-wars-tab-${children.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`
    : "alliance-wars-tab-button";

  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide -mb-px border-b-2 transition-colors flex items-center gap-2 ${
        active 
          ? "border-amber-500 text-amber-300" 
          : "border-transparent text-zinc-500 hover:text-zinc-300"
      }`}
    >
      {Icon && <Icon size={15} />} {children}
    </button>
  );
}

// Event Status Banner
function EventStatusBanner({ war, currentDay }) {
  const getTimeLeft = () => {
    if (war.status === "completed") return "Concluded";
    
    const now = new Date();
    let targetTime;
    
    if (war.status === "setup") {
      targetTime = new Date(war.lock_deadline);
    } else {
      targetTime = new Date(war.next_fight_at || war.first_fights_at);
    }
    
    const diff = targetTime - now;
    if (diff <= 0) return "Soon...";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };
  
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());
  
  useEffect(() => {
    if (war.status === "completed") return;
    const interval = setInterval(() => setTimeLeft(getTimeLeft()), 60000);
    return () => clearInterval(interval);
  }, [war]);
  
  return (
    <div className="relative overflow-hidden border border-amber-900/30 bg-zinc-950/50 p-5 mb-6 rounded-sm">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide font-manrope">
              {war.status === "completed" 
                ? "Event Complete"
                : `Day ${currentDay} of 10 — ${war.status === "setup" ? "Setup Phase" : "Battle Phase"}`}
            </div>
            <div className="font-cinzel text-base sm:text-lg text-amber-300 uppercase tracking-wide">
              {war.status === "setup" 
                ? "Leaders Submit Lineups" 
                : war.status === "completed"
                ? "Final Results"
                : `Round ${war.current_round} of 7`}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          {war.status !== "completed" && <Stat label="Next Phase" value={timeLeft} />}
          {war.status === "completed" && <Stat label="Status" value="Concluded" />}
          <Stat label="Alliances" value={war.participating_alliances?.length || 0} />
        </div>
      </div>
    </div>
  );
}

// Stat Component
function Stat({ icon: Icon, label, value }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1.5 mb-1">
        {Icon && <Icon className="text-amber-600" size={16} />}
        <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-manrope">{label}</div>
      </div>
      <div className="font-cinzel text-base sm:text-lg text-amber-400">{value}</div>
    </div>
  );
}

// Overview Tab
function OverviewTab({ war, myAlliance, isInWarAlliance }) {
  const isCompleted = war.status === "completed";
  
  return (
    <div className="space-y-6">
      {/* Warning for non-participants - only during setup/active */}
      {!isInWarAlliance && !isCompleted && (
        <div className="border border-amber-900/40 bg-amber-950/10 p-4 rounded-sm">
          <div className="flex items-center gap-3 text-amber-400/90 text-sm">
            <span className="font-manrope">
              {myAlliance 
                ? "Your alliance is not eligible for this event (requires 5+ members and 14+ gladiators across all members)"
                : "You must be in an alliance to participate in Alliance Wars"}
            </span>
          </div>
        </div>
      )}
      
      {/* Alliance Info - Only for participants */}
      {isInWarAlliance && (
        <div className="border border-amber-900/30 bg-zinc-950/50 p-6 rounded-sm">
          <h3 className="font-cinzel text-xs uppercase tracking-wide text-amber-500 mb-3">Your Alliance</h3>
          <div className="font-cinzel text-xl sm:text-2xl text-amber-300 uppercase tracking-wide">
            {myAlliance?.tag && <span className="text-zinc-600">[{myAlliance.tag}]</span>} {myAlliance?.name}
          </div>
        </div>
      )}
      
      {/* Quick Stats - Visible to everyone */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Event Duration Details */}
        <div className="border border-zinc-800 bg-zinc-950/50 p-5 rounded-sm">
          <div className="font-cinzel text-base sm:text-lg text-amber-400 uppercase tracking-wide mb-3">Event Duration</div>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="text-amber-500 font-cinzel text-lg font-bold mt-0.5">10</div>
              <div>
                <div className="text-sm text-zinc-300 font-semibold">Total Days</div>
                <div className="text-xs text-zinc-500 mt-1">3 days setup + 7 days battles</div>
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-400 font-manrope">Setup Phase:</span>
                <span className="text-amber-400 font-cinzel">Days 1-3</span>
              </div>
              <div className="text-[10px] text-zinc-400 ml-1">
                Leaders submit 14-fighter lineup (2 per round, max 3 per lanista). Owners set combat tactics for each class matchup.
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-400 font-manrope">Battle Phase:</span>
                <span className="text-amber-400 font-cinzel">Days 4-10</span>
              </div>
              <div className="text-[10px] text-zinc-400 ml-1">
                Daily battles at 5:00 PM CT. One round per day (7 rounds total).
              </div>
            </div>
          </div>
        </div>
        
        {/* Total Rounds Details */}
        <div className="border border-zinc-800 bg-zinc-950/50 p-5 rounded-sm">
          <div className="font-cinzel text-base sm:text-lg text-amber-400 uppercase tracking-wide mb-3">Battle Format</div>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="text-amber-500 font-cinzel text-lg font-bold mt-0.5">7</div>
              <div>
                <div className="text-sm text-zinc-300 font-semibold">Total Rounds</div>
                <div className="text-xs text-zinc-500 mt-1">One round per day, round-robin format</div>
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-400 font-manrope">Fighters Per Round:</span>
                <span className="text-amber-400 font-cinzel">2 Per Alliance</span>
              </div>
              <div className="text-[10px] text-zinc-400 ml-1">
                Each alliance fields 2 gladiators per round. Your fighters face every opponent from every rival alliance in that round.
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-400 font-manrope">Battle Schedule:</span>
                <span className="text-amber-400 font-cinzel">5:00 PM CT</span>
              </div>
              <div className="text-[10px] text-zinc-400 ml-1">
                All matchups resolve simultaneously. Results available immediately after.
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-amber-400 font-manrope font-bold">Combat Rules:</span>
                <span className="text-amber-400 font-cinzel font-bold">Gladiator Only</span>
              </div>
              <div className="text-[10px] text-zinc-400 ml-1">
                Battles are purely gladiator vs gladiator. All equipped <strong className="font-semibold text-zinc-300">gear</strong>, <strong className="font-semibold text-zinc-300">beasts</strong>, and <strong className="font-semibold text-zinc-300">skill trees</strong> are disabled — only raw stats and combat stances are utilized.
              </div>
            </div>
          </div>
        </div>
        
        {/* Prizes Section */}
        <div className="border border-zinc-800 bg-zinc-950/50 p-5 rounded-sm md:col-span-2">
          <div className="font-cinzel text-base sm:text-lg text-amber-400 uppercase tracking-wide mb-3">Prizes</div>
          <div className="space-y-2 text-xs text-zinc-400">
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 font-manrope">Winner:</span>
              <span className="text-amber-400 font-cinzel flex items-center gap-1.5">
                <img src="/gold-coin.png" alt="gold" className="w-3.5 h-3.5" />
                100g per member
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 font-manrope">Participation:</span>
              <span className="text-amber-400 font-cinzel flex items-center gap-1.5">
                <img src="/gold-coin.png" alt="gold" className="w-3.5 h-3.5" />
                10g per member
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 font-manrope">MVP Bonus:</span>
              <span className="text-amber-400 font-cinzel flex items-center gap-1.5">
                <img src="/gold-coin.png" alt="gold" className="w-3.5 h-3.5" />
                100g
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-zinc-800 bg-zinc-950/50 p-5 rounded-sm">
      {Icon && <Icon className="text-amber-500 mb-3" size={22} />}
      <div className="font-cinzel text-base sm:text-lg text-amber-400 uppercase tracking-wide">{value}</div>
      <div className="text-xs text-zinc-500 uppercase tracking-wide mt-1 font-manrope">{label}</div>
    </div>
  );
}

// Previous Wars Tab
function PreviousWars() {
  const [wars, setWars] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    api.get("/alliance-wars/history")
      .then(res => setWars(res.data.wars || []))
      .catch(() => toast.error("Failed to load war history"))
      .finally(() => setLoading(false));
  }, []);
  
  if (loading) return <Loader label="Loading war history..." />;
  
  if (!wars.length) {
    return (
      <div className="border border-zinc-800 bg-zinc-950/50 p-8 rounded-sm text-center">
        <History className="mx-auto mb-3 text-zinc-600" size={32} />
        <div className="text-zinc-500 font-manrope text-sm">No previous wars on record.</div>
      </div>
    );
  }
  
  return (
    <div data-testid="previous-wars" className="space-y-4">
      {wars.map((war, idx) => {
        const completedDate = war.completed_at 
          ? new Date(war.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : "Unknown";
        const topMvps = war.mvp?.slice(0, 3) || [];
        
        return (
          <div key={war.id} data-testid={`war-history-${idx}`} className="border border-amber-900/30 bg-zinc-950/50 rounded-sm overflow-hidden">
            {/* War Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800/60">
              <div className="flex items-center gap-3">
                <Trophy size={16} className="text-amber-500" />
                <span className="font-cinzel text-sm text-amber-300 uppercase tracking-wide">
                  Alliance War
                </span>
                <span className="text-[10px] text-zinc-600 font-mono">{war.id}</span>
              </div>
              <div className="text-xs text-zinc-500 font-manrope">{completedDate}</div>
            </div>
            
            {/* Winner */}
            <div className="px-5 py-4">
              <div className="text-[10px] text-zinc-600 uppercase tracking-widest font-manrope mb-1">
                {war.tied ? "Co-Champions" : "Champion"}
              </div>
              {war.tied ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {war.winners?.map((w, i) => (
                    <span key={w.id} className="inline-flex items-center gap-1">
                      {i > 0 && <span className="text-zinc-600 text-sm">&</span>}
                      <Link
                        to={`/alliance/${w.id}`}
                        className="inline-flex items-center gap-1.5 font-cinzel text-base text-amber-300 uppercase tracking-wide hover:text-amber-200 transition-colors"
                      >
                        {w.tag && <span className="text-zinc-500">[{w.tag}]</span>}
                        {w.name}
                        <ChevronRight size={14} className="text-amber-500/60" />
                      </Link>
                    </span>
                  ))}
                </div>
              ) : (
                <Link 
                  to={`/alliance/${war.winner_id}`}
                  data-testid={`war-history-winner-${idx}`}
                  className="inline-flex items-center gap-2 font-cinzel text-lg text-amber-300 uppercase tracking-wide hover:text-amber-200 transition-colors"
                >
                  {war.winner_tag && <span className="text-zinc-500">[{war.winner_tag}]</span>}
                  {war.winner_name}
                  <ChevronRight size={16} className="text-amber-500/60" />
                </Link>
              )}
              
              {/* MVP(s) */}
              {topMvps.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                  <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-manrope">MVP(s):</span>
                  {topMvps.map((m, mi) => (
                    <span key={mi} className="text-xs text-zinc-400 font-manrope">
                      {m.lanista_name || m.fighter_name} <span className="text-amber-500/70">({m.total_wins}W)</span>
                    </span>
                  ))}
                  {war.mvp?.length > 3 && (
                    <span className="text-xs text-zinc-600 font-manrope">+{war.mvp.length - 3} more</span>
                  )}
                </div>
              )}
            </div>
            
            {/* Final Rankings Row */}
            <div className="px-5 pb-4">
              <div className="flex flex-wrap gap-3">
                {war.rankings?.map((r, ri) => (
                  <Link
                    key={ri}
                    to={`/alliance/${r.alliance_id}`}
                    className="flex items-center gap-2 text-xs font-manrope hover:text-amber-300 transition-colors"
                  >
                    <span className={`font-cinzel font-bold ${
                      r.rank === 1 ? "text-amber-400" : r.rank === 2 ? "text-zinc-300" : "text-zinc-500"
                    }`}>
                      #{r.rank}
                    </span>
                    <span className="text-zinc-400">
                      {r.alliance_tag && `[${r.alliance_tag}] `}{r.alliance_name}
                    </span>
                    <span className="text-zinc-600">{r.total_points}pts</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Public Event View (for non-participants)
function PublicEventView({ war, currentDay, phaseLabel }) {
  return (
    <div className="space-y-6">
      {/* Quick Stats - Event Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Event Duration" value="10 Days" />
        <StatCard label="Total Rounds" value="7" />
        <div className="border border-zinc-800 bg-zinc-950/50 p-5 rounded-sm">
          <div className="font-cinzel text-base sm:text-lg text-amber-400 uppercase tracking-wide mb-3">Prizes</div>
          <div className="space-y-2 text-xs text-zinc-400">
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 font-manrope">Winner:</span>
              <span className="text-amber-400 font-cinzel flex items-center gap-1.5">
                <img src="/gold-coin.png" alt="gold" className="w-3.5 h-3.5" />
                100g per member
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 font-manrope">Participation:</span>
              <span className="text-amber-400 font-cinzel flex items-center gap-1.5">
                <img src="/gold-coin.png" alt="gold" className="w-3.5 h-3.5" />
                10g per member
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 font-manrope">MVP Bonus:</span>
              <span className="text-amber-400 font-cinzel flex items-center gap-1.5">
                <img src="/gold-coin.png" alt="gold" className="w-3.5 h-3.5" />
                100g
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Event Info */}
      <div className="border border-amber-900/30 bg-zinc-950/50 p-6 rounded-sm">
        <h3 className="font-cinzel text-xs uppercase tracking-wide text-amber-500 mb-4">Event Information</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-zinc-500 font-manrope">Current Phase:</span>
            <span className="font-cinzel text-amber-300 uppercase tracking-wide">{phaseLabel}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-zinc-500 font-manrope">Day:</span>
            <span className="font-cinzel text-amber-300 uppercase tracking-wide">{currentDay} of 10</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-zinc-500 font-manrope">Participating Alliances:</span>
            <span className="font-cinzel text-amber-300 uppercase tracking-wide">{war.participating_alliances?.length || 0}</span>
          </div>
          {war.status === "active" && (
            <div className="flex justify-between items-center">
              <span className="text-zinc-500 font-manrope">Current Round:</span>
              <span className="font-cinzel text-amber-300 uppercase tracking-wide">{war.current_round} of 7</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
